import { Mail, Phone, Smartphone, Building2, Users, Globe, MapPin, Compass, CalendarDays, StickyNote, ImageIcon } from "lucide-react";
import { SOURCE_OPTIONS } from "../clientConstants";

const sectionHeadCls = "font-mono text-xs uppercase tracking-[0.14em] font-bold text-[#0C2347] flex items-center gap-2 mb-3";
const fieldBoxCls = "rounded-lg border border-[#E4E9F0] bg-[#F8FAFC]/60 p-3.5 hover:border-[#0C2347]/30 transition-colors shadow-sm flex flex-col justify-start gap-1.5";
const fieldLabelCls = "text-xs font-semibold text-[#475467] flex items-center gap-1.5";
const fieldValueCls = "text-sm text-[#101828] font-medium leading-relaxed break-words";
const emptyCls = "text-sm text-[#94A3B8] italic font-normal";

function Field({ icon: Icon, label, value, testId, full }) {
  return (
    <div data-testid={testId} className={`${fieldBoxCls} ${full ? "sm:col-span-2" : ""}`}>
      <div className={fieldLabelCls}>{Icon && <Icon size={13} strokeWidth={2} />}{label}</div>
      <div className={value ? fieldValueCls : emptyCls}>{value || "Not provided"}</div>
    </div>
  );
}

export function OverviewPanel({ client: c }) {
  const addr = c.address || {};
  const addrLine = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  const source = SOURCE_OPTIONS.find((s) => s.value === c.client_source)?.label;
  const since = c.created_at ? new Date(c.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

  return (
    <div data-testid="client-overview-panel" className="space-y-7 max-w-5xl">
      {c.merged_into_name && (
        <div data-testid="client-merged-banner" className="rounded-lg bg-[#F1F2F4] border border-[#E4E9F0] px-4 py-3 text-sm text-[#51565F]">
          This client was merged into <span className="font-semibold text-[#101828]">{c.merged_into_name}</span>{c.merged_at ? ` on ${new Date(c.merged_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}. Their orders, invoices, deliveries, and notes now live under that client.
        </div>
      )}

      <section>
        <div className={sectionHeadCls}><Mail size={13} /> Contact &amp; identity</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div data-testid="client-overview-photo-tile" className={fieldBoxCls}>
            <div className={fieldLabelCls}><ImageIcon size={13} /> Profile photo</div>
            {c.photo_url
              ? <img data-testid="client-overview-photo-img" src={c.photo_url} alt="" className="mt-1 h-16 w-16 rounded-full object-cover border border-[#E4E9F0]" />
              : <div className={emptyCls}>No photo uploaded — add one from Edit client</div>}
          </div>
          <Field icon={Mail} label="Email" value={c.email} testId="client-overview-email" />
          <Field icon={Phone} label="Phone" value={c.phone} testId="client-overview-phone" />
          <Field icon={Smartphone} label="Secondary phone" value={c.secondary_phone} testId="client-overview-secondary-phone" />
        </div>
      </section>

      <section>
        <div className={sectionHeadCls}><Building2 size={13} /> Brokerage &amp; team</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div data-testid="client-overview-logo-tile" className={fieldBoxCls}>
            <div className={fieldLabelCls}><ImageIcon size={13} /> Brokerage logo</div>
            {c.logo_url
              ? <img data-testid="client-overview-logo-img" src={c.logo_url} alt="" className="mt-1 h-12 w-24 object-contain" />
              : <div className={emptyCls}>No logo uploaded — add one from Edit client</div>}
          </div>
          <Field icon={Building2} label="Company / brokerage" value={c.company} testId="client-overview-company" />
          <Field icon={Users} label="Team name" value={c.team_name} testId="client-overview-team" />
          <Field icon={Globe} label="Website" value={c.website} testId="client-overview-website" />
        </div>
      </section>

      <section>
        <div className={sectionHeadCls}><Compass size={13} /> Account &amp; source</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field icon={MapPin} label="Address" value={addrLine} testId="client-overview-address" />
          <Field icon={Compass} label="Client source" value={source} testId="client-overview-source" />
          <Field icon={CalendarDays} label="Client since" value={since} testId="client-overview-since" />
          <Field icon={StickyNote} label="Internal notes" value={c.internal_notes} testId="client-overview-internal-notes" full />
        </div>
      </section>
    </div>
  );
}
